import { useState } from 'react';

import { DocumentSendError, PueFault } from '@dreryk/egabinet-ezla-frontend-api';
import { Visit } from '@dreryk/egabinet-visit-frontend-api';

import { useFormController } from '@dreryk/react-common';
import { FormContent, StyledFlex } from '@dreryk/react-components';

import { guardianTranslationKeys, medicalLeaveFormTranslationKeys } from '../../../translations';
import { MedicalLeaveCommonFormController } from '../common/common.types';
import { isInsuredInForeign, isInsuredInPoland } from '../common/common.utils';
import { MedicalLeaveFormForeignPayerSection } from '../common/formSections/foreignPayer/MedicalLeaveFormPayerSection';
import { MedicalLeaveFormPayerSectionLoader } from '../common/formSections/foreignPayer/MedicalLeaveFormPayerSectionLoader';
import { MedicalLeaveFormLeaveDateRangeSection } from '../common/formSections/leaveDateRange/MedicalLeaveFormLeaveDateRangeSection';
import { MedicalLeaveFormPayerSection } from '../common/formSections/payer/MedicalLeaveFormPayerSection';
import { MedicalLeavePractitionerSection } from '../common/formSections/practitioner/MedicalLeavePractitionerSection';
import { MedicalLeaveFormStayAddressSection } from '../common/formSections/stayAdress/MedicalLeaveFormStayAddressSection';
import { MedicalLeavePueErrorsStatus } from '../common/MedicalLeaveErrorsStatus/MedicalLeavePueErrorsStatus';
import { StyledMedicalLeaveForm } from '../common/StyledMedicalLeaveForm';
import { StyledMedicalLeaveFormColumn } from '../common/StyledMedicalLeaveFormColumn';
import { MedicalLeaveZlaDocumentSendErrorsStatus } from '../medicalleave/MedicalLeaveDocumentErrorsStatus/MedicalLeaveZlaDocumentSendErrorsStatus';

import { GuardianMedicalLeaveSubmitAction } from './actionBar/GuardianMedicalLeaveSubmitAction/GuardianMedicalLeaveSubmitAction';
import { getGuardianMedicalLeaveFormInitialValues } from './guardianMedicalLeave.constants';
import { GuardianMedicalLeaveFormBean, MedicalLeaveForGuardianDraft } from './guardianMedicalLeave.types';
import { GuardianMedicalLeaveFormRouteLeavingGuard } from './GuardianMedicalLeaveFormRouteLeavingGuard';
import { GuardianDataSection } from './sections/guardianData/GuardianDataSection';

type GuardianMedicalLeaveFormProps = {
   readonly visit: Visit;
   readonly draft?: MedicalLeaveForGuardianDraft;
};

// eslint-disable-next-line max-lines-per-function
export const GuardianMedicalLeaveForm = (props: GuardianMedicalLeaveFormProps): JSX.Element => {
   const [pueFault, setPueFault] = useState<PueFault | undefined>();
   const [zlaDocumentSendErrors, setZlaDocumentSendErrors] = useState<DocumentSendError[]>([]);
   const { visit, draft } = props;
   // eslint-disable-next-line @typescript-eslint/no-unused-vars
   const { payers, leaveAfterTerminationOfEmployment, ...otherDraftValues } = draft || {};
   const formController = useFormController<GuardianMedicalLeaveFormBean>({
      ...getGuardianMedicalLeaveFormInitialValues(),
      payers:
         payers?.map((payer) => ({
            name: payer.name,
            identificationNumber: payer.identificationNumber?.identificationNumber,
            identificationNumberType: payer.identificationNumber?.identificationNumberType,
         })) ?? [],
      leaveAfterTerminationOfEmployment: leaveAfterTerminationOfEmployment ?? {},
      ...otherDraftValues,
   });
   const commonFieldsController = (formController as unknown) as MedicalLeaveCommonFormController;

   const { insuranceType, guardian } = formController.watch(['insuranceType', 'guardian']);

   return (
      <StyledFlex direction={'column'} gap={'12px'}>
         {pueFault ? <MedicalLeavePueErrorsStatus fault={pueFault} /> : null}
         {zlaDocumentSendErrors.length > 0 ? (
            <MedicalLeaveZlaDocumentSendErrorsStatus errors={zlaDocumentSendErrors} />
         ) : null}
         <StyledMedicalLeaveForm noValidate>
            <StyledMedicalLeaveFormColumn>
               <FormContent testId={'guardianMedicalLeave'}>
                  <MedicalLeaveFormLeaveDateRangeSection
                     controller={commonFieldsController}
                     draftValuesReason={draft?.reason ?? undefined}
                     title={guardianTranslationKeys.guardianLeaveDateRange}
                  />
                  {isInsuredInPoland(insuranceType) && (
                     <MedicalLeaveFormPayerSection
                        patientId={props.visit.patient.id}
                        controller={commonFieldsController}
                        externalIdentifier={guardian?.guardianExternalIdentifier}
                        noExternalIdentifierPlaceholder={guardianTranslationKeys.fillGuardianData}
                        title={guardianTranslationKeys.guardianPayersData}
                        onPueFault={setPueFault}
                     />
                  )}
                  {isInsuredInForeign(insuranceType) && (
                     <MedicalLeaveFormPayerSectionLoader patientId={visit.patient.id}>
                        {({ payersForeignRecentlyApproved }) => (
                           <MedicalLeaveFormForeignPayerSection
                              title={medicalLeaveFormTranslationKeys.payerData}
                              controller={formController}
                              payersForeignRecentlyApproved={payersForeignRecentlyApproved}
                           />
                        )}
                     </MedicalLeaveFormPayerSectionLoader>
                  )}
               </FormContent>
            </StyledMedicalLeaveFormColumn>
            <StyledMedicalLeaveFormColumn>
               <FormContent testId={'guardianStayAddress'}>
                  <GuardianDataSection
                     controller={formController}
                     initialGuardian={draft?.guardian ?? undefined}
                     patientId={visit.patient.id}
                     patientExternalIdentifier={visit.patient.externalIdentifier!}
                     shouldUseDefaultGuardianAddress={!draft?.address}
                  />
                  <MedicalLeaveFormStayAddressSection
                     title={guardianTranslationKeys.guardianStayAddress}
                     register={commonFieldsController.register}
                     watch={commonFieldsController.watch}
                  />
                  <MedicalLeavePractitionerSection />
               </FormContent>
            </StyledMedicalLeaveFormColumn>
         </StyledMedicalLeaveForm>

         <GuardianMedicalLeaveSubmitAction
            formController={formController}
            onFault={setPueFault}
            onZlaDocumentSendFault={setZlaDocumentSendErrors}
         />

         <GuardianMedicalLeaveFormRouteLeavingGuard
            active={formController.formState.isDirty}
            handleSubmitDraft={formController.handleSubmitDraft}
         />
      </StyledFlex>
   );
};
